import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail, type SupportedLanguage } from '@/utils/validation/email.validator';
import { validatePasswordStrength } from '@/utils/validation/password.validator';

/**
 * 폼 필드 유효성 검사 상태
 */
interface ValidationState {
  isValid: boolean;
  error?: string;
  hint?: string;
  isTouched: boolean;
  isDirty: boolean;
}

/**
 * 폼 유효성 검사 훅
 * @param language 언어 설정 (기본값: 'ko')
 */
export function useFormValidation(language: SupportedLanguage = 'ko') {
  const { t } = useTranslation('auth');
  const [validationStates, setValidationStates] = useState<Record<string, ValidationState>>({});

  /**
   * 필드 유효성 검사
   */
  const validateField = useCallback(
    (
      _fieldName: string,
      value: string,
      type: 'email' | 'password' | 'confirmPassword' | 'name' | 'phone'
    ) => {
      let isValid = true;
      let error: string | undefined;
      let hint: string | undefined;

      switch (type) {
        case 'email':
          const emailResult = validateEmail(value, true, language);
          isValid = emailResult.isValid;
          error = emailResult.error;
          hint = emailResult.isValid ? t('email_format_correct') : undefined;
          break;

        case 'password':
          const passwordResult = validatePasswordStrength(value);
          isValid = passwordResult.isValid;
          error = passwordResult.errors.length > 0 ? passwordResult.errors[0] : undefined;
          hint = passwordResult.isValid
            ? `${t('password_strong')} (${passwordResult.strength})`
            : `${t('password_strength')}: ${passwordResult.strength}`;
          break;

        case 'confirmPassword':
          // 비밀번호 확인은 별도 로직으로 처리 (validationStates에는 값이 저장되지 않음)
          isValid = value.length > 0; // 기본 검증만 수행
          error = !isValid ? t('confirm_password_required') : undefined;
          hint = isValid ? t('confirm_password_hint') : undefined;
          break;

        case 'name':
          isValid = value.length >= 2;
          error = !isValid ? t('name_too_short') : undefined;
          hint = isValid ? t('name_format_correct') : undefined;
          break;

        case 'phone':
          if (value.length > 0) {
            const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
            isValid = phoneRegex.test(value.replace(/[\s\-\(\)]/g, ''));
            error = !isValid ? t('phone_format_incorrect') : undefined;
            hint = isValid ? t('phone_format_correct') : undefined;
          } else {
            isValid = true; // 선택사항이므로 빈 값은 유효
          }
          break;

        default:
          isValid = value.length > 0;
          error = !isValid ? t('required_field') : undefined;
      }

      return { isValid, error, hint };
    },
    [validationStates, t]
  );

  /**
   * 필드 값 변경 핸들러
   */
  const handleFieldChange = useCallback(
    (
      fieldName: string,
      value: string,
      type: 'email' | 'password' | 'confirmPassword' | 'name' | 'phone'
    ) => {
      const currentState = validationStates[fieldName];
      const isDirty = currentState?.isDirty || false;

      const validation = validateField(fieldName, value, type);

      setValidationStates(prev => ({
        ...prev,
        [fieldName]: {
          ...validation,
          isTouched: currentState?.isTouched || false,
          isDirty: isDirty || value !== '',
        },
      }));
    },
    [validationStates, validateField]
  );

  /**
   * 필드 포커스 아웃 핸들러
   */
  const handleFieldBlur = useCallback(
    (
      fieldName: string,
      value: string,
      type: 'email' | 'password' | 'confirmPassword' | 'name' | 'phone'
    ) => {
      const currentState = validationStates[fieldName];
      const isDirty = currentState?.isDirty || false;

      const validation = validateField(fieldName, value, type);

      setValidationStates(prev => ({
        ...prev,
        [fieldName]: {
          ...validation,
          isTouched: true,
          isDirty,
        },
      }));
    },
    [validationStates, validateField]
  );

  /**
   * 전체 폼 유효성 검사
   */
  const validateForm = useCallback(
    (formData: Record<string, string>) => {
      const errors: Record<string, string> = {};
      let isFormValid = true;

      Object.entries(formData).forEach(([fieldName, _value]) => {
        const fieldState = validationStates[fieldName];
        if (fieldState && !fieldState.isValid) {
          errors[fieldName] = fieldState.error || t('invalid_value');
          isFormValid = false;
        }
      });

      return { isFormValid, errors };
    },
    [validationStates, t]
  );

  /**
   * 특정 필드의 유효성 상태 반환
   */
  const getFieldState = useCallback(
    (fieldName: string): ValidationState => {
      return (
        validationStates[fieldName] || {
          isValid: true,
          isTouched: false,
          isDirty: false,
        }
      );
    },
    [validationStates]
  );

  /**
   * 필드 상태 초기화
   */
  const resetFieldState = useCallback((fieldName: string) => {
    setValidationStates(prev => {
      const newStates = { ...prev };
      delete newStates[fieldName];
      return newStates;
    });
  }, []);

  /**
   * 모든 필드 상태 초기화
   */
  const resetAllStates = useCallback(() => {
    setValidationStates({});
  }, []);

  return {
    validationStates,
    handleFieldChange,
    handleFieldBlur,
    validateForm,
    getFieldState,
    resetFieldState,
    resetAllStates,
  };
}
